import { addSpacesToTitleCase, enumKeyOf, enumKeys } from "common/helpers/utils";
import Guid from "common/values/guid/guid";
import { PermissionsByGranteeId } from "permissions/entities/permission/api/permissions-api-service";
import Permission from "permissions/entities/permission/permission";
import { AccessLevel } from "permissions/entities/permission/resources/general";
import { ProposalAccess, WorkAgreementField } from "permissions/entities/permission/resources/work-resources";
import { CardType, GroupedCellConfig, MetaControlType } from "permissions/entities/permission/view/components/permissions-table/types";
import { getProposalName, isPermissionsRow, isTemplateRow } from "permissions/entities/permission/view/components/permissions-table/utils";
import { JSX } from "react";
import BarebonesProposalInfoAPIResponse from "work/entities/proposal/api/response-contracts/barebones-proposal-info-api-response";
import { ProposalError } from "work/entities/proposal/store/proposals-redux-slice";

export default function ProposalGroupCell({
  row,
  loadingProposalIds,
  allProposalErrors,
  barebonesProposals,
  renderGroupedPermission,
}: {
  row: PermissionsByGranteeId | null;
  loadingProposalIds: Guid[];
  allProposalErrors: ProposalError[];
  barebonesProposals: BarebonesProposalInfoAPIResponse[];
  renderGroupedPermission: (row: PermissionsByGranteeId, config: GroupedCellConfig) => JSX.Element;
}): JSX.Element | null {
  if (!row?.recentPermissionsPreview) return null;
  // Determine dynamic title (single proposal -> show its name)
  const proposalResources = row.recentPermissionsPreview
    .filter((p): p is Permission & { resource: ProposalAccess } => p.resource instanceof ProposalAccess)
    .map(p => p.resource);

  const uniqueProposalIds = Array.from(
    new Set(proposalResources.map(r => r.proposalId?.value).filter((v): v is string => !!v))
  );
  const title = (() => {
    if (uniqueProposalIds.length !== 1) return undefined;
    const resWithId =
      proposalResources.find(r => r.proposalId?.value === uniqueProposalIds[0])
      ?? proposalResources.find(r => !!r.proposalId);
    return resWithId
      ? getProposalName(resWithId, loadingProposalIds, allProposalErrors, barebonesProposals)
      : undefined;
  })();

  return renderGroupedPermission(row, {
    cardType: CardType.Grouped,
    keys: enumKeys(WorkAgreementField).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
    candidatesForKey: (row, waKey) => {
      if (isPermissionsRow(row)) {
        return row.recentPermissionsPreview.filter((permission) => {
          if (!(permission.resource instanceof ProposalAccess)) return false;
          const fieldKey = WorkAgreementField[waKey as keyof typeof WorkAgreementField];
          const fields = permission.resource.fields ?? {};
          return Object.hasOwn(fields, fieldKey);
        });
      }
      if (isTemplateRow(row)) {
        return row.resources.filter((resource) => {
          if (!(resource instanceof ProposalAccess)) return false;
          const fieldKey = WorkAgreementField[waKey as keyof typeof WorkAgreementField];
          const fields = resource.fields ?? {};
          return Object.hasOwn(fields, fieldKey);
        });
      }
      return [];
    },
    labelForKey: (waKey) => addSpacesToTitleCase(waKey),
    controlMetaForResource: (resource, waKey) => {
      const fieldKey = WorkAgreementField[waKey as keyof typeof WorkAgreementField];
      const fields = (resource as ProposalAccess).fields as Partial<Record<WorkAgreementField, AccessLevel>> ?? {};
      const current = fields[fieldKey];
      const opts = enumKeys(AccessLevel);
      const value = current ? enumKeyOf(AccessLevel, current) : opts[0];
      return { type: MetaControlType.AccessLevel, label: "Access Level", options: opts, value };
    },
    title,
    colSpan: 2,
    rowSpan: 2,
    canEdit: true
  });
}
